import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errorMessage } from "../utils/errorMessage";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Stage G2: Onboarding Link Health admin page.
 *
 * Lists every matter past Send-OB-Link stage where:
 *   - No onboarding_link_sent log entry for primary, or
 *   - Joint clients exist but no log entries for one or more, or
 *   - Link sent but no OB completion within 7 days, or
 *   - Email send failed.
 *
 * "Resend" button per row → /api/admin/onboarding/{matter_ref}/resend-link.
 * Surfaces success/failure honestly — no false-success toast.
 */
export default function OnboardingLinkHealth() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendingByRef, setResendingByRef] = useState({});
  const [error, setError] = useState("");
  const [threshold, setThreshold] = useState(7);

  const userId = sessionStorage.getItem("bonsai_user_id") || "";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API}/api/admin/audit/onboarding-link-status`,
        {
          params: { days_threshold: threshold },
          headers: { "X-Bonsai-User-Id": userId },
        }
      );
      setRows(res.data.rows || []);
    } catch (e) {
      const msg = errorMessage(e, "Load failed");
      setError(msg);
      toast.error(`Failed to load OB-link health: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [userId, threshold]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (mref) => {
    setResendingByRef((s) => ({ ...s, [mref]: true }));
    try {
      const res = await axios.post(
        `${API}/api/admin/onboarding/${encodeURIComponent(mref)}/resend-link`,
        {},
        { headers: { "X-Bonsai-User-Id": userId } }
      );
      const errs = res.data.errors || [];
      const ok = res.data.resent_to || [];
      if (errs.length > 0 && ok.length === 0) {
        toast.error(`Resend failed for ${mref}: ${errs[0].error}`);
      } else if (errs.length > 0) {
        toast.warning(`Resent ${ok.length} link(s); ${errs.length} failed`);
      } else if (ok.length === 0) {
        toast.info(res.data.message || "All recipients already have a send recorded");
      } else {
        toast.success(`Resent ${ok.length} link(s) for ${mref}`);
      }
      await load();
    } catch (e) {
      const msg = errorMessage(e, "Resend failed");
      toast.error(`Resend failed for ${mref}: ${msg}`);
    } finally {
      setResendingByRef((s) => ({ ...s, [mref]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] p-6" data-testid="onboarding-link-health-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-teal">Onboarding Link Health</h1>
            <p className="text-sm text-slate-600">
              Matters past Send-OB-Link stage where the link is missing or stale.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Threshold (days):</label>
            <input
              type="number"
              min={1}
              max={60}
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value, 10) || 7)}
              className="w-20 px-2 py-1 text-sm border border-slate-300 rounded-md"
              data-testid="threshold-input"
            />
            <Button
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="refresh-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="ob-link-health-table">
            <thead className="bg-slate-100 text-slate-700 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Matter ref</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">Fee earner</th>
                <th className="px-3 py-2 text-left">Sent at</th>
                <th className="px-3 py-2 text-left">Days</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Missing</th>
                <th className="px-3 py-2 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-slate-400">
                    No matters flagged. The pipeline is healthy 🎉
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.matter_reference} className="border-t border-slate-100" data-testid={`row-${r.matter_reference}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.matter_reference}</td>
                  <td className="px-3 py-2">{r.client_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.fee_earner || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.link_sent_at ? new Date(r.link_sent_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.days_since_send ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.send_failed && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">send failed</span>}
                    {!r.send_failed && r.threshold_breached && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">stale</span>}
                    {!r.send_failed && !r.threshold_breached && r.missing_recipients?.length > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">missing</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(r.missing_recipients || []).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      onClick={() => handleResend(r.matter_reference)}
                      disabled={!!resendingByRef[r.matter_reference]}
                      data-testid={`resend-${r.matter_reference}`}
                    >
                      {resendingByRef[r.matter_reference] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                      Resend
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
